import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UtilsService } from 'src/app/services/utils.service';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { WeeklyHoursRequirement } from 'src/app/enums/weekly-hours-requirement.enum';
import { CompanyProductPhaseEditDialogComponent } from './company-product-phase-edit-dialog/company-product-phase-edit-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CompanyProductPhaseStage } from 'src/app/models/company_product_phase_stage.model';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SecondsToStringTimePipe } from 'src/app/pipes/seconds-to-string-time.pipe';
import { AvailabilityTreeNode } from 'src/app/models/availability_tree.model';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { CompanyProductTeam } from 'src/app/models/company_product_team.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { UserListDialogComponent } from 'src/app/components/main/shared/user-list-dialog/user-list-dialog.component';
import { UserListDialogData } from 'src/app/models/user-list-dialog.model';
import { buildCompanyProductMemberRoleBadges, indexCompanyProductMembersByUserId } from 'src/app/utils/company-product-member-role-badges';

@Component({
  selector: 'app-company-product-phases',
  templateUrl: './company-product-phases.component.html',
  styleUrls: ['./company-product-phases.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatProgressBarModule,
    SecondsToStringTimePipe,
    MatTooltip,
    CompanyProductPageIdentityComponent,
  ],
})
export class CompanyProductPhasesComponent extends IndexTemplateComponent {
  protected fb: UntypedFormBuilder;
  protected activatedRoute: ActivatedRoute;
  private api = inject(ApiService);
  protected router: Router;
  protected utils: UtilsService;
  private permissions = inject(PermissionsService);
  private dialog = inject(MatDialog);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly WeeklyHoursRequirement = WeeklyHoursRequirement;

  displayed_columns: string[] = ['type', 'team', 'actions'];
  data_source: MatTableDataSource<CompanyProductPhase> = new MatTableDataSource<CompanyProductPhase>();

  filterForm: UntypedFormGroup;
  company_product_id: string;
  company_product_phase_stages: CompanyProductPhaseStage[] = [];
  weekly_progress_loading = false;

  isWorker: boolean = false;
  isPartner: boolean = false;
  isClient: boolean = false;

  private readonly permissions_company_product_id = (() => {
    const route = inject(ActivatedRoute);
    return route.snapshot.parent?.paramMap.get('company_product_id') ?? route.parent?.snapshot.paramMap.get('id') ?? '';
  })();

  readonly can_act_as_product_manager = toSignal(
    this.permissions_company_product_id
      ? this.api
          .getCompanyProductUserPermissions(this.permissions_company_product_id)
          .pipe(map(permissions => permissions.can_act_as_product_manager))
      : of(false),
    { initialValue: false }
  );

  override filterFormExtraParams: any = {
    company_product_phase_stage_id: ['is_active'],
  };

  private destroy_ref = inject(DestroyRef);
  private list_subscription: Subscription | null = null;
  private weekly_progress_subscription: Subscription | null = null;
  private product_members_by_user_id = new Map<number, CompanyProductUserMember>();

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const activatedRoute = inject(ActivatedRoute);
    const router = inject(Router);
    const utils = inject(UtilsService);

    super(router, fb, activatedRoute, utils);

    this.fb = fb;
    this.activatedRoute = activatedRoute;
    this.router = router;
    this.utils = utils;
  }

  override ngOnInit(): void {
    if (this.activatedRoute.snapshot.parent?.paramMap.get('company_product_id') != null)
      this.company_product_id = this.activatedRoute.snapshot.parent?.paramMap.get('company_product_id') as string;
    else if (this.activatedRoute.parent?.snapshot.paramMap.get('id'))
      this.company_product_id = this.activatedRoute.parent?.snapshot.paramMap.get('id') as string;

    super.ngOnInit();
    this.fetchCompanyProductPhaseStages();
    this.fetchProductMembers();
    this.initPermissions();

    if (this.isWorker) {
      this.displayed_columns.splice(2, 0, 'week-progress');
    }
  }

  override fetchData() {
    this.cancelListRequest();
    this.cancelWeeklyProgressRequest();

    const params = this.buildListParams({
      with: 'contract,company_product_phase_stage,company_product_phase_type,company_product_team..users',
      boards: true,
    });

    this.list_subscription = this.api
      .getCompanyProductPhasesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(data => {
        this.data_source.data = data.data;
        this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);

        if (this.isWorker) {
          this.fetchWeeklyProgressCached();
        }
      });
  }

  private fetchWeeklyProgressCached(): void {
    this.cancelWeeklyProgressRequest();
    this.weekly_progress_loading = true;

    const params = this.buildListParams({
      append: 'weekly_progress_cached',
    });

    this.weekly_progress_subscription = this.api
      .getCompanyProductPhasesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: data => {
          const weekly_progress_by_id = new Map<number, AvailabilityTreeNode | undefined>(
            data.data.map(company_product_phase => [company_product_phase.id, company_product_phase.weekly_progress_cached])
          );

          this.data_source.data = this.data_source.data.map(company_product_phase => ({
            ...company_product_phase,
            weekly_progress_cached: weekly_progress_by_id.get(company_product_phase.id),
          }));
          this.weekly_progress_loading = false;
        },
        error: () => {
          this.weekly_progress_loading = false;
        },
      });
  }

  private buildListParams(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const params: Record<string, unknown> = {
      ...this.filterForm.value,
      company_product_id: this.company_product_id,
      ...extra,
    };

    if ('company_product_phase_stage_id' in params && params['company_product_phase_stage_id'] === 'is_active') {
      params['is_active'] = true;
      delete params['company_product_phase_stage_id'];
    }

    return params;
  }

  private cancelListRequest(): void {
    this.list_subscription?.unsubscribe();
    this.list_subscription = null;
  }

  private cancelWeeklyProgressRequest(): void {
    this.weekly_progress_subscription?.unsubscribe();
    this.weekly_progress_subscription = null;
    this.weekly_progress_loading = false;
  }

  fetchCompanyProductPhaseStages() {
    this.api.getCompanyProductPhaseStages().subscribe(data => {
      this.company_product_phase_stages = data;
    });
  }

  openEditCompanyProductPhaseDialog(companyProductPhase: CompanyProductPhase) {
    const dialogRef = this.dialog.open(CompanyProductPhaseEditDialogComponent, {
      width: '600px',
      data: {
        company_product_phase: companyProductPhase,
      },
    });

    dialogRef.afterClosed().subscribe((data: any) => {
      if (data != null) {
        this.updateCompanyProductPhase(companyProductPhase.id, data);
      }
    });
  }

  openTeamMembersDialog(team: CompanyProductTeam, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const users = team.users ?? [];
    const dialog_data: UserListDialogData = {
      title: team.name,
      subtitle: `${users.length} ${users.length === 1 ? 'miembro' : 'miembros'}`,
      users: users.map(user => ({
        id: user.id,
        full_name: user.full_name || `${user.name} ${user.surnames}`.trim(),
        pokemon_profile_picture_url: user.pokemon_profile_picture_url ?? null,
        serious_profile_picture_url: user.serious_profile_picture_url ?? null,
        badges: buildCompanyProductMemberRoleBadges(this.product_members_by_user_id.get(user.id)),
      })),
    };

    this.dialog.open(UserListDialogComponent, {
      data: dialog_data,
      width: '520px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'user-list-dialog',
    });
  }

  private fetchProductMembers(): void {
    if (!this.company_product_id) {
      return;
    }

    this.api
      .getBisualProductionTeam(this.company_product_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(members => {
        this.product_members_by_user_id = indexCompanyProductMembersByUserId(members);
      });
  }

  updateCompanyProductPhase(companyProductPhaseId: number, data: any) {
    this.api.updateCompanyProductPhase(companyProductPhaseId, data).subscribe(() => {
      this.fetchData();
    });
  }

  private initPermissions(): void {
    this.isWorker = this.permissions.isWorker();
    this.isPartner = this.permissions.isPartner();
    this.isClient = this.permissions.isClient();
  }
}
